import React from 'react';
import { toast } from 'react-hot-toast';
import { LuCircleCheck, LuCopy, LuInfo, LuShieldAlert, LuSparkles, LuTriangleAlert } from 'react-icons/lu';

import { getRiskFlagMeta } from '../../shared/utils/risk-flag-labels';

type Sentiment = 'positive' | 'neutral' | 'negative' | string;
type RiskLevel = 'low' | 'medium' | 'high' | string;

type Props = {
  riskFlags?: string[];
  sentiment?: Sentiment;
  riskLevel?: RiskLevel;
  statId?: string;
};

function riskMeta(riskLevel?: RiskLevel) {
  const r = (riskLevel || 'medium').toLowerCase();
  if (r === 'high') {
    return {
      stripe: 'border-l-error',
      bg: 'bg-error/5',
      badge: 'badge-error',
      icon: <LuShieldAlert className="text-xl" />,
      riskLabel: 'High risk',
    };
  }
  if (r === 'low') {
    return {
      stripe: 'border-l-success',
      bg: 'bg-success/5',
      badge: 'badge-success',
      icon: <LuCircleCheck className="text-xl" />,
      riskLabel: 'Low risk',
    };
  }
  return {
    stripe: 'border-l-warning',
    bg: 'bg-warning/5',
    badge: 'badge-warning',
    icon: <LuTriangleAlert className="text-xl" />,
    riskLabel: 'Medium risk',
  };
}

function sentimentBadgeClass(sentiment?: Sentiment) {
  const s = (sentiment || 'neutral').toLowerCase();
  if (s === 'positive') return 'badge-success';
  if (s === 'negative') return 'badge-error';
  return 'badge-ghost';
}

export const StatInsightsPanel: React.FC<Props> = ({ riskFlags, sentiment, riskLevel, statId }) => {
  if (!riskFlags?.length) return null;

  const meta = riskMeta(riskLevel);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(riskFlags.map((f) => getRiskFlagMeta(f).label).join(', '));
      toast.success('Copied to clipboard', {
        duration: 2000,
        icon: <LuCopy />,
      });
    } catch {
      // ignore
    }
  };

  return (
    <div className={`card bg-base-100 border-l-4 ${meta.stripe} shadow-card overflow-hidden`}>
      <div className={`card-body p-4 ${meta.bg}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="mt-0.5 opacity-80">{meta.icon}</div>

            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="inline-flex items-center gap-2">
                  <LuSparkles className="opacity-70" />
                  <span className="font-semibold">Insights</span>
                </div>
                <span className={`badge ${meta.badge}`}>{meta.riskLabel}</span>
                <span className={`badge ${sentimentBadgeClass(sentiment)}`}>
                  {String(sentiment || 'neutral').toLowerCase()}
                </span>
                {statId && <span className="badge badge-ghost">#{statId.slice(-6)}</span>}
              </div>

              <ul className="space-y-2">
                {riskFlags.map((flag) => {
                  const { label, description } = getRiskFlagMeta(flag);
                  return (
                    <li key={flag} className="flex items-start gap-2">
                      <LuTriangleAlert className="mt-0.5 shrink-0 text-sm opacity-60" />
                      <div>
                        <span className="text-sm font-medium">{label}</span>
                        {description && <p className="text-xs opacity-60 mt-0.5">{description}</p>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <button className="btn btn-ghost btn-sm btn-circle shrink-0" onClick={copyToClipboard} title="Copy">
            <LuCopy />
          </button>
        </div>

        <div className="mt-3 flex items-start gap-2 text-xs opacity-70">
          <LuInfo className="mt-0.5 shrink-0" />
          <div>
            These insights are generated automatically. Use them to spot risks early and decide where to drill down
            next.
          </div>
        </div>
      </div>
    </div>
  );
};

// backwards-compat alias
export const StatAIPanel = StatInsightsPanel;

export default StatInsightsPanel;
